import { useState, useEffect, useCallback } from 'react';
import {
  Video, Search as SearchIcon, Crosshair, AlertTriangle, ImageOff,
  LayoutGrid, Table as TableIcon,
} from 'lucide-react';
import { videoSearch, videoSearchHealth, videoThumbUrl } from '../../services/api';
import { cropHighlightStyle, cropDescription } from '../../utils/videoSearchCrops';
import styles from './SimilarityLabPage.module.css';

/**
 * Video search over indexed sessions.
 *
 * Three query modes share one result list:
 *   - text        CLIP text->image similarity
 *   - similar     nearest neighbours of a hit you already found
 *   - telemetry   pure SQL over the driving state, no model involved
 *
 * The telemetry mode is not a fallback. "Hardest cornering" and "near this point"
 * are exact, while text search is approximate — so the two are labelled
 * differently in the results rather than presented as one ranked list.
 */

// CLIP text->image cosine similarity sits in a much narrower band than people
// expect from a "score": ~0.15 unrelated, ~0.30 a strong match. Showing the raw
// number without that context invites reading 0.24 as a poor result.
const TEXT_SCORE_FLOOR = 0.15;
const TEXT_SCORE_CEIL = 0.32;

const PRESETS = [
  { label: 'On track', text: 'a car driving fast on a racetrack' },
  { label: 'Paddock / pit lane', text: 'a car stopped in the pit lane or paddock' },
  { label: 'Following a car', text: 'driving close behind another race car' },
  { label: 'Wet track', text: 'driving in the rain on a wet track' },
];

const TELEMETRY_PRESETS = [
  { label: 'Hardest cornering', filters: { min_abs_lat_accel: 1.0 }, order_by: 'lat_accel' },
  { label: 'Top speed', filters: { min_speed_kmh: 100 }, order_by: 'speed_kmh' },
  { label: 'Hard left-handers', filters: { max_lat_accel: -0.9 }, order_by: 'lat_accel' },
];

function fmtTime(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtNum(v, digits = 1) {
  return v == null || Number.isNaN(v) ? '—' : v.toFixed(digits);
}

/** The raw score, signed for contrast because it straddles zero. */
function fmtScore(score, scoreMode) {
  if (score == null) return '—';
  return scoreMode === 'contrast'
    ? (score >= 0 ? '+' : '') + score.toFixed(3)
    : score.toFixed(3);
}

/** What the number actually means, for the column header and tooltips.
 *
 *  The API returns 1 - cosine_distance, so higher is better. Calling it a distance
 *  in the UI would invert how people read it.
 */
function scoreLabel(scoreMode) {
  return scoreMode === 'contrast' ? 'Sides − ahead' : 'Cosine';
}

function scoreTitle(score, scoreMode) {
  if (score == null) return undefined;
  return scoreMode === 'contrast'
    ? `max(L,R) − C = ${score.toFixed(4)} (cosine similarities)`
    : `cosine similarity ${score.toFixed(4)} — 1.0 is identical, ~0.15 unrelated`;
}

/** Normalise CLIP's narrow similarity band into something a bar can show.
 *
 * `contrast` is a difference of two similarities, not a similarity: it straddles
 * zero and spans roughly ±0.2, so putting it through the text-image band would
 * render every result as an empty or full bar. It gets its own scale.
 */
function scoreFraction(score, mode, scoreMode) {
  if (score == null) return null;
  if (scoreMode === 'contrast') {
    return Math.max(0, Math.min(1, (score + CONTRAST_SPAN) / (2 * CONTRAST_SPAN)));
  }
  if (mode === 'similar') return Math.max(0, Math.min(1, score));
  const span = TEXT_SCORE_CEIL - TEXT_SCORE_FLOOR;
  return Math.max(0, Math.min(1, (score - TEXT_SCORE_FLOOR) / span));
}

const CONTRAST_SPAN = 0.2;

const CROP_CHOICES = [
  { key: 'L', label: 'Left' },
  { key: 'C', label: 'Ahead' },
  { key: 'R', label: 'Right' },
];

const PER_SESSION_CHOICES = [
  { value: 1, label: '1' },
  { value: 3, label: '3' },
  { value: 6, label: '6' },
  { value: null, label: 'All' },
];

/** How each filter key reads as a removable chip. */
const FILTER_LABELS = {
  lap_number: v => `Lap ${v}`,
  min_speed_kmh: v => `≥ ${v} km/h`,
  max_speed_kmh: v => `≤ ${v} km/h`,
  min_lat_accel: v => `lat g ≥ ${v}`,
  max_lat_accel: v => `lat g ≤ ${v}`,
  min_abs_lat_accel: v => `|lat g| ≥ ${v}`,
  min_throttle_pct: v => `throttle ≥ ${v}%`,
  max_throttle_pct: v => `throttle ≤ ${v}%`,
  min_sync_confidence: v => `sync ≥ ${v}`,
  near_lat: () => null,          // shown once, by radius_m
  near_lon: () => null,
  radius_m: v => `within ${v} m`,
};

/** Active filters as [key, text] pairs, skipping the ones with no standalone meaning. */
function describeFilters(filters) {
  return Object.entries(filters || {})
    .filter(([, v]) => v != null)
    .map(([k, v]) => [k, (FILTER_LABELS[k] || (x => `${k}: ${x}`))(v)])
    .filter(([, text]) => text);
}

export default function VideoSearch({ onLoadSession, viewUserId, viewUserName }) {
  const [health, setHealth] = useState(undefined);   // undefined = still checking
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('text');
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState('score');
  const [hits, setHits] = useState([]);
  const [searched, setSearched] = useState(false);
  const [sessionsSearched, setSessionsSearched] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Which parts of the frame may match, and how the three combine.
  const [crops, setCrops] = useState(['L', 'C', 'R']);
  const [scoreMode, setScoreMode] = useState('max');
  // Cards suit browsing, the table suits comparing scores across many hits.
  // Remembered because it is a working preference, not a per-search choice.
  const [view, setView] = useState(
    () => localStorage.getItem('vtrSearchView') || 'grid');
  const [limit, setLimit] = useState(24);
  // How many hits one session may contribute. This, not `limit`, is usually what
  // decides how many results come back: three sessions matching at three apiece is
  // nine hits no matter how high the limit goes.
  const [perSession, setPerSession] = useState(3);
  // The scoring mode the displayed hits were produced with, which is not
  // necessarily the one currently selected.
  const [resultScoreMode, setResultScoreMode] = useState('max');

  useEffect(() => {
    setHealth(undefined);
    setHits([]);
    setSearched(false);
    videoSearchHealth(viewUserId).then(setHealth);
  }, [viewUserId]);

  // Contrast subtracts the centre crop from the sides, so it is meaningless with
  // only the centre selected — and VTR rejects it outright. Fall back rather than
  // let the user assemble a request that 422s.
  useEffect(() => {
    if (scoreMode === 'contrast' && !crops.includes('L') && !crops.includes('R')) {
      setScoreMode('max');
    }
  }, [crops, scoreMode]);

  const run = useCallback(async (body, nextMode) => {
    setLoading(true);
    setError(null);
    try {
      const result = await videoSearch(body);
      setHits(result.hits || []);
      setSessionsSearched(result.sessions_searched ?? 0);
      setMode(nextMode);
      setResultScoreMode(body.score_mode || 'max');
      setSearched(true);
    } catch (e) {
      setError(String(e.message || e));
      setHits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const runText = useCallback((text) => {
    const phrase = (text ?? query).trim();
    if (!phrase) return;
    setQuery(phrase);
    run({ text: phrase, filters, limit, max_per_session: perSession,
          user_id: viewUserId, crops, score_mode: scoreMode }, 'text');
  }, [query, filters, run, viewUserId, crops, scoreMode, limit, perSession]);

  const runTelemetry = useCallback((preset) => {
    const f = preset ? preset.filters : filters;
    const ob = preset ? preset.order_by : orderBy;
    setFilters(f);
    setOrderBy(ob);
    run({ filters: f, order_by: ob, limit, max_per_session: perSession,
          user_id: viewUserId }, 'telemetry');
  }, [filters, orderBy, run, viewUserId, limit, perSession]);

  const runSimilar = useCallback((hit) => {
    // Carry the crop that matched, so "more like this" means more of whatever was
    // found there rather than more of the scene in general.
    run({ similar_to: hit.id, similar_to_crop: hit.matched_crop || undefined,
          limit, max_per_session: perSession, user_id: viewUserId,
          crops, score_mode: scoreMode }, 'similar');
  }, [run, viewUserId, crops, scoreMode, limit, perSession]);

  const clearFilter = useCallback((key) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      // The geo filter is all-or-nothing upstream, so its three keys go together.
      if (key === 'radius_m') { delete next.near_lat; delete next.near_lon; }
      return next;
    });
  }, []);

  const chooseView = useCallback((next) => {
    setView(next);
    try { localStorage.setItem('vtrSearchView', next); } catch { /* private mode */ }
  }, []);

  const toggleCrop = useCallback((key) => {
    setCrops(prev => {
      const next = prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key];
      // Never leave nothing selected; VTR would reject it and the user would have
      // to guess why.
      return next.length ? ['L', 'C', 'R'].filter(c => next.includes(c)) : prev;
    });
  }, []);

  if (health === undefined) {
    return <div className={styles.card}>Checking video search…</div>;
  }
  if (health && health.available === false) {
    return (
      <div className={styles.card}>
        <AlertTriangle size={16} /> Video search is unavailable: {health.reason}
      </div>
    );
  }
  if (!health) {
    return <div className={styles.card}>Video search is unavailable.</div>;
  }

  const textDisabled = !health.text_search;
  const sidesSelected = crops.includes('L') || crops.includes('R');
  // Visual search returns nothing while the index is being rebuilt under a new
  // crop geometry. Saying so beats reporting every query as "no matches".
  const rebuilding = health.similarity_search === false && health.indexed_rows > 0;
  const activeFilters = describeFilters(filters);

  return (
    <div className={styles.card}>
      <div style={{ marginBottom: 12 }}>
        <strong><Video size={16} /> Video search</strong>
        <div className={styles.muted} style={{ marginTop: 4 }}>
          {health.indexed_sessions} of {viewUserId ? `${viewUserName || 'this driver'}'s` : 'your'}
          {' '}{health.owned_sessions} sessions indexed
          {' · '}{health.indexed_rows?.toLocaleString?.() ?? 0} searchable moments
          {textDisabled && ' · text search unavailable (no model loaded)'}
        </div>
        {health.owned_sessions > health.indexed_sessions && (
          <div className={styles.hint} style={{ marginTop: 4 }}>
            Only rendered sessions are searchable — a session is indexed after it
            is rendered with a confident sync.
          </div>
        )}
      </div>

      {/* Telemetry queries are exact, so they lead. */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
          <Crosshair size={12} /> By what the car was doing — exact
        </div>
        <div className={styles.segmented}>
          {TELEMETRY_PRESETS.map(p => (
            <button key={p.label} className={styles.seg} disabled={loading}
                    onClick={() => runTelemetry(p)}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Filters set by a preset carry into the next text search, so they have to
          be visible — an invisible constraint reads as a broken search. */}
      {activeFilters.length > 0 && (
        <div className={styles.filterChips}>
          <span className={styles.controlLabel}>Filtering on</span>
          {activeFilters.map(([key, text]) => (
            <span key={key} className={styles.chip}>
              {text}
              <button className={styles.chipX} title="Remove this filter"
                      onClick={() => clearFilter(key)}>×</button>
            </span>
          ))}
          <button className={styles.seg} onClick={() => setFilters({})}>Clear all</button>
        </div>
      )}

      {/* Text search is approximate; labelled so. */}
      <div style={{ marginBottom: 10, opacity: textDisabled ? 0.5 : 1 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
          <SearchIcon size={12} /> By what the video looks like — approximate
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text" value={query} disabled={textDisabled || loading}
            placeholder="e.g. a car in the pit lane"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runText(); }}
            style={{ flex: 1, padding: '6px 8px' }}
          />
          <button className={styles.seg} disabled={textDisabled || loading}
                  onClick={() => runText()}>Search</button>
        </div>
        <div className={styles.segmented} style={{ marginTop: 6 }}>
          {PRESETS.map(p => (
            <button key={p.label} className={styles.seg}
                    disabled={textDisabled || loading}
                    onClick={() => runText(p.text)}>{p.label}</button>
          ))}
        </div>

        {/* Each frame is indexed as three overlapping squares, so a query can be
            aimed at part of the image rather than the whole thing. */}
        <div className={styles.cropControls}>
          <div>
            <div className={styles.controlLabel}>Where to look</div>
            <div className={styles.segmented}>
              {CROP_CHOICES.map(c => (
                <button key={c.key}
                        className={crops.includes(c.key)
                          ? `${styles.seg} ${styles.segOn}` : styles.seg}
                        disabled={textDisabled || loading}
                        aria-pressed={crops.includes(c.key)}
                        onClick={() => toggleCrop(c.key)}>{c.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className={styles.controlLabel}>Scoring</div>
            <div className={styles.segmented}>
              <button className={scoreMode === 'max'
                        ? `${styles.seg} ${styles.segOn}` : styles.seg}
                      disabled={textDisabled || loading}
                      onClick={() => setScoreMode('max')}>Best view</button>
              <button className={scoreMode === 'contrast'
                        ? `${styles.seg} ${styles.segOn}` : styles.seg}
                      disabled={textDisabled || loading || !sidesSelected}
                      title={sidesSelected ? undefined
                        : 'Needs Left or Right selected — it compares them against Ahead'}
                      onClick={() => setScoreMode('contrast')}>Beside the car</button>
            </div>
          </div>
        </div>
        {scoreMode === 'contrast' && (
          <div className={styles.hint} style={{ marginTop: 4 }}>
            Ranks by how much better the sides match than the road ahead — for
            finding things you drove past rather than things you drove toward.
          </div>
        )}
      </div>

      {/* How many results, and how they may be distributed. The two interact, and
          the second is usually the one that binds. */}
      <div className={styles.cropControls}>
        <div>
          <div className={styles.controlLabel}>Results: {limit}</div>
          <input type="range" min={10} max={50} step={5} value={limit}
                 disabled={loading} className={styles.slider}
                 onChange={e => setLimit(Number(e.target.value))} />
        </div>
        <div>
          <div className={styles.controlLabel}>Max per session</div>
          <div className={styles.segmented}>
            {PER_SESSION_CHOICES.map(c => (
              <button key={c.label}
                      className={perSession === c.value
                        ? `${styles.seg} ${styles.segOn}` : styles.seg}
                      disabled={loading} aria-pressed={perSession === c.value}
                      onClick={() => setPerSession(c.value)}>{c.label}</button>
            ))}
          </div>
        </div>
      </div>
      {perSession != null && limit > perSession * Math.max(1, health.indexed_sessions) && (
        <div className={styles.hint} style={{ marginTop: 4 }}>
          {perSession} per session across {health.indexed_sessions} indexed session
          {health.indexed_sessions === 1 ? '' : 's'} caps this at{' '}
          {perSession * health.indexed_sessions} — raise "max per session" for more.
        </div>
      )}

      {loading && <div className={styles.hint}>Searching…</div>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && searched && hits.length === 0 && (
        <div className={styles.empty}>
          {rebuilding && mode !== 'telemetry' ? (
            <>Visual search is rebuilding its index — telemetry queries still work.</>
          ) : (
            <>
              No matches in {sessionsSearched} session{sessionsSearched === 1 ? '' : 's'}.
              {mode === 'text' && ' Text search is approximate — try a telemetry query.'}
            </>
          )}
        </div>
      )}

      {hits.length > 0 && (
        <>
          <div className={styles.resultsHeader}>
            {mode === 'text' ? (
              <div className={styles.hint}>
                Cosine similarity; higher is better.
              </div>
            ) : <span />}
            <div className={styles.segmented}>
              <button className={view === 'grid' ? `${styles.seg} ${styles.segOn}` : styles.seg}
                      title="Thumbnail grid" aria-pressed={view === 'grid'}
                      onClick={() => chooseView('grid')}>
                <LayoutGrid size={13} /> Cards
              </button>
              <button className={view === 'table' ? `${styles.seg} ${styles.segOn}` : styles.seg}
                      title="Compact table with scores" aria-pressed={view === 'table'}
                      onClick={() => chooseView('table')}>
                <TableIcon size={13} /> Table
              </button>
            </div>
          </div>

          {view === 'table' ? (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th />
                  <th style={{ textAlign: 'left' }}>Session</th>
                  <th>Lap</th>
                  <th>Time</th>
                  <th>Speed</th>
                  <th>Lat g</th>
                  {mode !== 'telemetry' && <th>Crop</th>}
                  {mode !== 'telemetry' && (
                    <th style={{ textAlign: 'right' }}>{scoreLabel(resultScoreMode)}</th>
                  )}
                  <th />
                </tr>
              </thead>
              <tbody>
                {hits.map(h => {
                  const thumb = videoThumbUrl(h.thumb_small_path, viewUserId);
                  const open = onLoadSession
                    ? () => onLoadSession(h.session_key, h.csv_time)
                    : undefined;
                  return (
                    <tr key={h.id}>
                      <td className={styles.rowThumb}>
                        {thumb && (
                          <img src={thumb} alt="" width={72} height={41} loading="lazy"
                               onClick={open}
                               title={open ? 'Open this session at this moment' : undefined} />
                        )}
                      </td>
                      <td title={h.session_key}>{h.session_key.slice(0, 8)}</td>
                      <td style={{ textAlign: 'center' }}>{h.lap_number ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtTime(h.video_time)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(h.speed_kmh, 0)} km/h</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(h.lat_accel, 2)}</td>
                      {mode !== 'telemetry' && (
                        <td style={{ textAlign: 'center' }}
                            title={h.matched_crop
                              ? `Matched on the ${cropDescription(h.matched_crop)}` : undefined}>
                          {h.matched_crop ?? '—'}
                        </td>
                      )}
                      {mode !== 'telemetry' && (
                        <td className={styles.scoreCell}
                            title={scoreTitle(h.score, resultScoreMode)}>
                          {fmtScore(h.score, resultScoreMode)}
                        </td>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className={styles.seg} disabled={loading}
                                onClick={() => runSimilar(h)}>Similar</button>
                        {open && (
                          <button className={styles.seg} onClick={open}>Open</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          ) : (
          <div className={styles.hitGrid}>
            {hits.map(h => {
              const frac = scoreFraction(h.score, mode, resultScoreMode);
              const thumb = videoThumbUrl(h.thumb_small_path, viewUserId);
              const highlight = cropHighlightStyle(h.matched_crop);
              // The seek target is telemetry time: VideoPanel adds the sync lead
              // offset itself, so passing video_time would apply it twice.
              const open = onLoadSession
                ? () => onLoadSession(h.session_key, h.csv_time)
                : undefined;
              return (
                <div key={h.id} className={styles.hitCard}>
                  <div className={styles.hitThumb}
                       role={open ? 'button' : undefined}
                       tabIndex={open ? 0 : undefined}
                       title={open ? 'Open this session at this moment' : undefined}
                       onClick={open}
                       onKeyDown={open ? (e => {
                         if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                       }) : undefined}>
                    {thumb ? (
                      <img src={thumb} alt="" width={320} height={180} loading="lazy" />
                    ) : (
                      <div className={styles.hitNoThumb}>
                        <ImageOff size={18} />
                        <span>no frame stored</span>
                      </div>
                    )}
                    {highlight && (
                      <span className={styles.cropBox} style={highlight}
                            title={`Matched on the ${cropDescription(h.matched_crop)}`}>
                        <span className={styles.cropTag}>{h.matched_crop}</span>
                      </span>
                    )}
                    <span className={styles.hitTime}>{fmtTime(h.video_time)}</span>
                  </div>

                  <div className={styles.hitMeta}>
                    <span title={h.session_key}>{h.session_key.slice(0, 8)}</span>
                    <span>Lap {h.lap_number ?? '—'}</span>
                    <span>{fmtNum(h.speed_kmh, 0)} km/h</span>
                    <span>{fmtNum(h.lat_accel, 2)} g</span>
                  </div>

                  {mode !== 'telemetry' && frac != null && (
                    <div className={styles.scoreRow}
                         title={scoreTitle(h.score, resultScoreMode)}>
                      <div className={styles.scoreBar}>
                        <div style={{ width: `${frac * 100}%` }} />
                      </div>
                      <span className={styles.scoreValue}>
                        {fmtScore(h.score, resultScoreMode)}
                      </span>
                    </div>
                  )}

                  <div className={styles.hitActions}>
                    <button className={styles.seg} disabled={loading}
                            title={h.matched_crop
                              ? `Find more like the ${cropDescription(h.matched_crop)} of this frame`
                              : 'Find visually similar moments'}
                            onClick={() => runSimilar(h)}>Similar</button>
                    {open && (
                      <button className={styles.seg} onClick={open}
                              title="Open this session at this moment">Open</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}
    </div>
  );
}
